/**
 * ContactsList - the director's Contacts list in the Stealth column (2026-10-21: "the GM, made easy").
 *
 * Every hidden figure by name, with its bearing from the party and the four levels the players may know it at:
 * Unknown (nothing anywhere - where every hidden figure starts), Signs (a slow ring on the tracker), Contact (a
 * blip on its bearing and a "?" where it was last seen, still hidden), and Revealed - which is no level: it is the
 * reveal itself, the same verb as the tab menu's (hooks/useHideReveal), so the figure leaves the list. Two buttons
 * move the whole list: every Unknown to Signs, and everyone back to Unknown.
 *
 * Drawn only on the director's screen: the names are the GM's (the hook's list is empty anywhere else).
 *
 * @param {object} props
 * @param {object} props.contacts - hooks/useStealthContacts' `contacts`: list, setLevel, allUnknownToSigns,
 *   allToUnknown
 * @param {(id: string) => void} [props.onReveal] - the GM's reveal; absent, no Revealed button
 */
import clsx from '../../utils/clsx';
import common from '../../styles/common.module.css';
import story from '../../styles/Story.module.css';
import styles from '../../styles/Stealth.module.css';

const LEVELS = [
  { level: 'unknown', label: 'Unknown', hint: 'Nothing on any screen' },
  { level: 'signs', label: 'Signs', hint: 'A slow ring on the tracker' },
  { level: 'contact', label: 'Contact', hint: 'A blip on its bearing and a ? where it stands, still hidden' },
];

function Levels({ figure, contacts, onReveal }) {
  const pick = (level) => contacts.setLevel(figure.id, level);
  return (
    <div className={styles.levels} role="group" aria-label={`What the players know of ${figure.name}`}>
      {LEVELS.map(({ level, label, hint }) => (
        <button
          key={level}
          type="button"
          title={hint}
          className={clsx(figure.level === level && styles.levelOn)}
          aria-pressed={figure.level === level}
          data-testid={`contact-level-${figure.id}-${level}`}
          onClick={() => pick(level)}
        >
          {label}
        </button>
      ))}
      {onReveal && (
        <button
          type="button"
          title="Reveal them: on the map and in the order, for everyone"
          aria-pressed={false}
          data-testid={`contact-level-${figure.id}-revealed`}
          onClick={() => onReveal(figure.id)}
        >
          Revealed
        </button>
      )}
    </div>
  );
}

export default function ContactsList({ contacts, onReveal }) {
  const list = contacts?.list ?? [];
  if (!list.length) return null;
  return (
    <section className={story.section} aria-label="Contacts" data-testid="contacts-list">
      <h3 className={story.caption}>Contacts · what they know</h3>
      <div className={story.tools}>
        <button
          type="button"
          className={common.btnSmall}
          data-testid="contacts-all-signs"
          onClick={contacts.allUnknownToSigns}
        >
          All Unknown to Signs
        </button>
        <button
          type="button"
          className={common.btnSmall}
          data-testid="contacts-all-unknown"
          onClick={contacts.allToUnknown}
        >
          All to Unknown
        </button>
      </div>
      {list.map((figure) => (
        <div
          key={figure.id}
          className={styles.contactRow}
          data-testid={`contact-row-${figure.id}`}
          data-level={figure.level}
        >
          <div className={styles.contactTop}>
            <span className={styles.contactName}>{figure.name}</span>
            <span className={styles.contactBearing}>{figure.compass ?? 'off the map'}</span>
          </div>
          <Levels figure={figure} contacts={contacts} onReveal={onReveal} />
        </div>
      ))}
    </section>
  );
}
